import { Icon } from 'components/Icon';
import { CANCELLED_STATUS, installmentLabel } from 'utils/billing';

// Etiqueta de renegociacao na coluna Contrato, compartilhada pelas telas do
// menu Cobranca. Mesmo motivo do `alertAction.js`: a origem do contrato nao
// muda conforme o degrau da esteira em que ele esta sendo olhado. A tela faz
// o spread junto dos outros helpers:
//
//   overrides: { ...buildRenegotiationOverride(), ...buildHistoryOverride(...) }
//
// Os campos vem do `contractFields` (utils/billing.js): `renegotiationOrigin`
// (a original, na cobranca nova), `renegotiations` (as geradas, na original),
// `hasRenegotiatedTitles` e `hasOpenTitles`. Os dados usados em cada
// renegociacao ficam na aba Renegociacao do modal de Historico.

const PURPLE = '#7B44F2';
const GREY = '#718096';
// `error` da paleta: o `red` (#FF3B30) e forte demais para etiqueta.
const ERROR = '#dd4952';

const TAG_STYLES = {
  origin: { color: PURPLE, background: 'rgba(123, 68, 242, 0.1)' },
  renegotiated: { color: GREY, background: 'rgba(113, 128, 150, 0.12)' },
  cancelled: { color: ERROR, background: 'rgba(221, 73, 82, 0.1)' },
};

const listContracts = (items) =>
  (items || []).map((item) => item?.contrato || `#${item?.id}`).join(', ');

// Etiquetas da linha. Uma cobranca pode ter as duas: nasceu de uma
// renegociacao e depois foi renegociada de novo.
//
// Renegociacao cancelada nao conta: o backend mantem o item em
// `renegociacoes` (com `status: cancelado`) para o historico nao perder o
// vinculo, mas a original voltou a ser cobrada por inteiro. Ela so aparece no
// `title` de uma etiqueta que ja exista por outro motivo.
export const getRenegotiationTags = (row) => {
  const tags = [];
  const origin = row?.renegotiationOrigin;
  if (origin) {
    const originContract = origin.contrato || `#${origin.id}`;
    if (row?.status === CANCELLED_STATUS) {
      tags.push({
        key: 'cancelled',
        label: 'Renegociação cancelada',
        icon: 'block',
        title: `Renegociação do contrato ${originContract} cancelada: os títulos voltaram para a cobrança original`,
      });
    } else {
      const parcelas = row?.parcelas_origem
        ? `, parcelas ${String(row.parcelas_origem).split(';').join(', ')}`
        : '';
      tags.push({
        key: 'origin',
        label: 'Renegociação',
        icon: 'autorenew',
        title: `Originada da renegociação do contrato ${originContract}${parcelas}`,
      });
    }
  }
  const renegotiations = row?.renegotiations || [];
  const active = renegotiations.filter(
    (item) => item?.status !== CANCELLED_STATUS
  );
  const cancelled = renegotiations.filter(
    (item) => item?.status === CANCELLED_STATUS
  );
  if (row?.hasRenegotiatedTitles || active.length) {
    const generated = active.length ? ` — gerou ${listContracts(active)}` : '';
    const cancelledNote = cancelled.length
      ? ` — ${cancelled
          .map((item) => `${item?.contrato || `#${item?.id}`} (cancelada)`)
          .join(', ')}`
      : '';
    // Com titulo em aberto, as parcelas restantes seguem sendo cobradas aqui.
    tags.push({
      key: 'renegotiated',
      label: row?.hasOpenTitles ? 'Renegociada parcialmente' : 'Renegociada',
      icon: 'call_split',
      title: `${
        row?.hasOpenTitles
          ? 'Parte das parcelas foi renegociada; as demais continuam em cobrança'
          : 'Todas as parcelas foram renegociadas'
      }${generated}${cancelledNote}`,
    });
  }
  return tags;
};

export const RenegotiationTag = ({ variant, label, icon, title }) => (
  <span
    title={title}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '3px',
      padding: '1px 8px 1px 5px',
      borderRadius: '999px',
      fontSize: '11px',
      fontWeight: 600,
      whiteSpace: 'nowrap',
      cursor: 'default',
      ...TAG_STYLES[variant],
    }}
  >
    <Icon name={icon} size={14} />
    {label}
  </span>
);

const ContractCell = ({ row }) => {
  const tags = getRenegotiationTags(row);
  return (
    <span
      style={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: '4px 6px',
      }}
    >
      {row?.contrato || null}
      {tags.map((tag) => (
        <RenegotiationTag
          key={tag.key}
          variant={tag.key}
          label={tag.label}
          icon={tag.icon}
          title={tag.title}
        />
      ))}
    </span>
  );
};

// Baixas que nao sao pagamento: aparecem na visao Baixados da Carteira e nao
// podem passar por pagas. Nas telas da esteira as linhas sao vencidas, entao
// as marcas nunca saem.
const INSTALLMENT_TAGS = {
  renegociado: {
    variant: 'renegotiated',
    label: 'Renegociado',
    icon: 'call_split',
    title: 'Parcela baixada por renegociação, sem pagamento',
  },
  // Titulo da cobranca cancelada: baixado com valor_recebido 0.
  cancelado: {
    variant: 'cancelled',
    label: 'Cancelado',
    icon: 'block',
    title: 'Parcela baixada pelo cancelamento da renegociação, sem pagamento',
  },
};

// Renegociada com a mora dispensada: os campos de mora seguem preenchidos com
// o que era devido, entao a parcela avisa que ela nao foi cobrada.
const WAIVED_TAG = {
  variant: 'renegotiated',
  label: 'Mora dispensada',
  icon: 'money_off',
  title:
    'Juros, multa e IOF de atraso desta parcela foram dispensados na renegociação',
};

const InstallmentCell = ({ row }) => {
  const tag = INSTALLMENT_TAGS[row?.tipo_baixa];
  if (!tag) {
    return installmentLabel(row) ?? null;
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
      {installmentLabel(row)}
      <RenegotiationTag {...tag} />
      {row?.tipo_baixa === 'renegociado' && row?.mora_dispensada ? (
        <RenegotiationTag {...WAIVED_TAG} />
      ) : null}
    </span>
  );
};

export const buildRenegotiationOverride = () => ({
  contrato: { renderCell: ContractCell },
  parcela: { renderCell: InstallmentCell },
});
